import { phoneCountries } from "../data/phoneCountries";

const DEFAULT_ISO2 = "US";

const normalizeDigits = (value = "") => String(value || "").replace(/\D/g, "");

const buildFlagUrl = (iso2 = DEFAULT_ISO2) => `https://flagcdn.com/w40/${String(iso2 || DEFAULT_ISO2).toLowerCase()}.png`;

const countryCatalog = (phoneCountries || []).map((country) => ({
	...country,
	iso2: String(country?.code || "").toUpperCase(),
	dialCode: normalizeDigits(country?.phone || ""),
}));

const DEFAULT_COUNTRY =
	countryCatalog.find((country) => country.iso2 === DEFAULT_ISO2) || countryCatalog[0] || { iso2: DEFAULT_ISO2, dialCode: "1", label: "United States" };

const countriesByDialLengthDesc = [...countryCatalog]
	.filter((country) => country.dialCode)
	.sort((a, b) => b.dialCode.length - a.dialCode.length);

export const getDefaultPhoneCountry = () => DEFAULT_COUNTRY;

export const findPhoneCountryByIso2 = (iso2 = "") => {
	const normalizedIso = String(iso2 || "").trim().toUpperCase();
	if (!normalizedIso) return null;
	return countryCatalog.find((country) => country.iso2 === normalizedIso) || null;
};

export const findPhoneCountryByLabel = (label = "") => {
	const normalizedLabel = String(label || "").trim().toLowerCase();
	if (!normalizedLabel) return null;
	return countryCatalog.find((country) => String(country?.label || "").trim().toLowerCase() === normalizedLabel) || null;
};

export const findPhoneCountryByDialCode = (dialCode = "") => {
	const digits = normalizeDigits(dialCode);
	if (!digits) return null;
	// For NANP shared code (+1), default to US unless a stronger signal exists (label/iso2).
	if (digits === "1") {
		return findPhoneCountryByIso2("US") || null;
	}
	return countryCatalog.find((country) => country.dialCode === digits) || null;
};

export const detectPhoneCountryFromValue = (value = "", fallbackCountry = null) => {
	const digits = normalizeDigits(value);
	if (!digits) return fallbackCountry || DEFAULT_COUNTRY;

	const preferred = fallbackCountry && digits.startsWith(fallbackCountry.dialCode) ? fallbackCountry : null;
	if (preferred) return preferred;

	const detected = countriesByDialLengthDesc.find((country) => digits.startsWith(country.dialCode));
	return detected || fallbackCountry || DEFAULT_COUNTRY;
};

export const resolvePhoneCountry = ({ iso2, dialCode, label, fallbackIso2 = DEFAULT_ISO2 } = {}) => {
	return (
		findPhoneCountryByIso2(iso2) ||
		findPhoneCountryByLabel(label) ||
		findPhoneCountryByDialCode(dialCode) ||
		findPhoneCountryByIso2(fallbackIso2) ||
		DEFAULT_COUNTRY
	);
};

/**
 * Format a phone value as "+<dial> <national digits>" for a country.
 *
 * maxLocalDigits defaulted to 10 -- a NANP assumption applied to every country,
 * silently truncating longer numbers. It now defaults to the E.164 ceiling, so
 * formatting cannot lose digits; rejecting is validation's job.
 *
 * @param {string} value Raw user input, any format.
 * @param {object|string|null} countryInput Country object, ISO2, label or dial code.
 * @param {number|null} [maxLocalDigits] Override the national-digit cap.
 * @returns {string} e.g. "+86 13426469409", or "+86" when no digits were given.
 */
export const normalizePhoneForCountry = (value = "", countryInput = null, maxLocalDigits = null) => {
	const country =
		typeof countryInput === "string"
			? resolvePhoneCountry({ iso2: countryInput, label: countryInput, dialCode: countryInput })
			: countryInput || DEFAULT_COUNTRY;

	const digits = normalizeDigits(value);
	if (!digits) return `+${country.dialCode}`;

	let localDigits = digits;
	const detectedCountry = detectPhoneCountryFromValue(value, country);
	if (detectedCountry?.dialCode && localDigits.startsWith(detectedCountry.dialCode)) {
		localDigits = localDigits.slice(detectedCountry.dialCode.length);
	} else if (country?.dialCode && localDigits.startsWith(country.dialCode)) {
		localDigits = localDigits.slice(country.dialCode.length);
	}

	// E.164 caps a full number at 15 digits including the country code.
	const cap = Number.isFinite(maxLocalDigits) && maxLocalDigits > 0
		? maxLocalDigits
		: Math.max(1, 15 - String(country?.dialCode || "").length);
	localDigits = localDigits.slice(0, cap);
	return localDigits ? `+${country.dialCode} ${localDigits}` : `+${country.dialCode}`;
};

export const getNationalDigits = (value = "", countryInput = null) => {
	const country =
		typeof countryInput === "string"
			? resolvePhoneCountry({ iso2: countryInput, label: countryInput, dialCode: countryInput })
			: countryInput || DEFAULT_COUNTRY;
	const digits = normalizeDigits(value);
	if (!digits) return "";
	if (country?.dialCode && digits.startsWith(country.dialCode)) {
		return digits.slice(country.dialCode.length);
	}
	return digits;
};

// National significant number bounds, applied to every country. E.164 caps a
// full number at 15 digits including the dial code; no plan is shorter than 6.
//
// A plain range, not a per-country table: a table drifts as carriers add ranges,
// and every gap in it is a real customer who cannot register -- which is the bug
// being fixed here, where a hardcoded 10 locked out every Chinese mobile.
const NSN_MIN = 6;
const NSN_MAX = 15;

/**
 * Longest national number a country can hold, i.e. E.164's 15 minus its dial code.
 *
 * Exported so input components cap typing from the same rule the validator uses.
 * PhoneInput previously hardcoded 10 in two places, which is what physically
 * blocked the 11th digit; a cap that disagrees with the validator is worse than
 * none, because the field refuses input and shows no error.
 *
 * @param {object|string|null} countryInput Country object, ISO2, label or dial code.
 * @returns {number} Maximum national digits for that country.
 */
export const getMaxNationalDigits = (countryInput = null) => {
	const country =
		typeof countryInput === "string"
			? resolvePhoneCountry({ iso2: countryInput, label: countryInput, dialCode: countryInput })
			: countryInput || DEFAULT_COUNTRY;
	// 15 total digits minus this country's dial code.
	return Math.max(1, NSN_MAX - String(country?.dialCode || "").length);
};

/**
 * Validate a phone number's national part against the E.164 digit range.
 *
 * The country comes from the value's own dial code, so callers pass nothing new
 * and the cap tracks the selected country rather than a fixed 10.
 *
 * @param {string} value Phone value, ideally "+<dial> <national>".
 * @param {{minDigits?: number, maxDigits?: number}} [bounds] Outer sanity bounds
 *   on the full digit string, applied before the national-part check.
 * @returns {{ok: boolean, reason: "ok"|"required"|"too_short"|"too_long", digits: number}}
 */
export const validateInternationalPhone = (value = "", { minDigits = 7, maxDigits = 15 } = {}) => {
	const digits = normalizeDigits(value);
	if (!digits) return { ok: false, reason: "required", digits: 0 };
	if (digits.length < minDigits) return { ok: false, reason: "too_short", digits: digits.length };
	if (digits.length > maxDigits) return { ok: false, reason: "too_long", digits: digits.length };

	const country = detectPhoneCountryFromValue(value);
	const national = getNationalDigits(value, country);
	// No national part means the user typed only a dial code.
	if (!national) return { ok: false, reason: "too_short", digits: digits.length };

	if (national.length < NSN_MIN) return { ok: false, reason: "too_short", digits: digits.length };
	if (national.length > getMaxNationalDigits(country)) return { ok: false, reason: "too_long", digits: digits.length };

	return { ok: true, reason: "ok", digits: digits.length };
};

export const getPhoneMetaForCountry = (countryInput = null) => {
	const country =
		typeof countryInput === "string"
			? resolvePhoneCountry({ iso2: countryInput, label: countryInput, dialCode: countryInput })
			: countryInput || DEFAULT_COUNTRY;
	return {
		phone_country_code: country.dialCode,
		phone_country_iso2: country.iso2,
		phone_country_flag: buildFlagUrl(country.iso2),
	};
};

export const resolvePhoneState = ({
	phoneValue,
	countryLabel,
	phoneCountryIso2,
	phoneCountryCode,
	fallbackIso2 = DEFAULT_ISO2,
} = {}) => {
	const resolvedCountry = resolvePhoneCountry({
		iso2: phoneCountryIso2,
		dialCode: phoneCountryCode,
		label: countryLabel,
		fallbackIso2,
	});

	return {
		country: resolvedCountry,
		phoneValue: normalizePhoneForCountry(phoneValue || "", resolvedCountry),
		meta: getPhoneMetaForCountry(resolvedCountry),
	};
};

export { buildFlagUrl, normalizeDigits };
